import { authenticatedInstance } from '../index/index.service';
import type { IdResponse, TenantUserRole, TenantView, UserAdminView } from 'shared-models/browser';
import { extractServiceErrorMessage } from '../shared/service-error.util';

export type { TenantUserRole, UserAdminView } from 'shared-models/browser';

export type CreateUserRequest = {
  displayName: string;
  username: string;
  email: string;
  password: string;
  role: TenantUserRole;
  tenantIds: string[];
};

export type UpdateUserRequest = {
  displayName?: string;
  password?: string;
  role?: TenantUserRole;
  tenantIds?: string[];
};

export type TenantBootstrapView = TenantView;

export type UsersBootstrapResponse = {
  users: UserAdminView[];
  tenants: TenantBootstrapView[];
};

class UsersService {
  // Summary: Fetches legacy users-only list for compatibility with old consumers that still expect array-only response shape.
  // Details: docs/function-library/web/services.md#users-service
  async listOnly(): Promise<UserAdminView[]> {
    try {
      const response = await authenticatedInstance.get<UserAdminView[]>('/users/list');
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Fetches canonical users-page payload from /users (users+tenants) for lower request fanout and clearer route semantics.
  // Details: docs/function-library/web/services.md#users-service
  async listUsersPage(): Promise<UsersBootstrapResponse> {
    try {
      const response = await authenticatedInstance.get<UsersBootstrapResponse>('/users');
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Sends super-admin user provisioning requests with tenant memberships to backend.
  // Details: docs/function-library/web/services.md#users-service
  async create(payload: CreateUserRequest): Promise<IdResponse> {
    try {
      const response = await authenticatedInstance.post<IdResponse>('/users', payload);
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Sends super-admin updates for existing user profile and tenant memberships.
  // Details: docs/function-library/web/services.md#users-service
  async update(userId: number, payload: UpdateUserRequest): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/users/${userId}`, payload);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Sends super-admin deactivation requests to disable specific user accounts.
  // Details: docs/function-library/web/services.md#users-service
  async deactivate(userId: number): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/users/${userId}/deactivate`);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }

  // Summary: Sends super-admin activation requests to restore specific user accounts.
  // Details: docs/function-library/web/services.md#users-service
  async activate(userId: number): Promise<void> {
    try {
      await authenticatedInstance.patch<void>(`/users/${userId}/activate`);
    } catch (error: unknown) {
      throw new Error(extractServiceErrorMessage(error, ''));
    }
  }
}

export const usersService = new UsersService();





